import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faInfoCircle, faSpinner, faCheck, faTimes, faFile, faDownload, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import '../styles/DataSources.css';
import ApiService from '../utils/ApiService';
import { getAuth } from 'firebase/auth';

// Tooltip component for helping users understand features
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span className="tooltip-container">
    <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
    <span className="tooltip-text">{text}</span>
  </span>
);

interface UploadStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  message?: string;
  documentId?: number;
}

interface Document {
  id: number;
  filename: string;
  file_size: number;
  content_type: string;
  created_at: string;
}

const DataSources: React.FC = () => {
  const [uploadQueue, setUploadQueue] = useState<UploadStatus[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalDocuments: 0,
    limit: 10,
  });

  // Fetch documents with pagination
  const fetchDocuments = useCallback(async (page = 0) => {
    try {
      setIsLoading(true);
      setError(null);
      const limit = pagination.limit;
      const skip = page * limit;
      const response = await ApiService.getUserDocuments(skip, limit);
      setDocuments(response.documents);
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalDocuments: response.total,
      }));
      setIsLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch documents';
      setError(errorMessage);
      setIsLoading(false);
    }
  }, [pagination.limit]);

  // Fetch initial documents when component mounts
  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Process file uploads when queue changes
  useEffect(() => {
    const uploadPendingFiles = async () => {
      const pendingFile = uploadQueue.find(f => f.status === 'pending');
      if (!pendingFile) return;
      setUploadQueue(prev => 
        prev.map(item => 
          item.file.name === pendingFile.file.name 
            ? { ...item, status: 'uploading' } 
            : item
        )
      );
      try {
        // Call API to upload document (no token needed)
        const response = await ApiService.uploadDocument(pendingFile.file);
        // ...existing success logic...
        setUploadQueue(prev => 
          prev.map(item => 
            item.file.name === pendingFile.file.name 
              ? { ...item, status: 'success', documentId: response.id } 
              : item
          )
        );
        if (pagination.currentPage === 0) {
          setDocuments(prev => [
            {
              id: response.id,
              filename: response.filename,
              file_size: response.file_size,
              content_type: response.content_type,
              created_at: response.created_at
            },
            ...prev,
          ]);
          setPagination(prev => ({
            ...prev,
            totalDocuments: prev.totalDocuments + 1
          }));
        } else {
          fetchDocuments(pagination.currentPage);
        }
      } catch (uploadError) {
        const errorMessage = uploadError instanceof Error ? uploadError.message : 'Upload failed';
        setUploadQueue(prev => 
          prev.map(item => 
            item.file.name === pendingFile.file.name 
              ? { ...item, status: 'error', message: errorMessage } 
              : item
          )
        );
      }
    };
    if (uploadQueue.some(f => f.status === 'pending')) {
      uploadPendingFiles();
    }
  }, [uploadQueue, pagination.currentPage, fetchDocuments]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const fileList = Array.from(event.target.files);
      
      // Add files to upload queue with pending status
      const newFiles = fileList.map(file => ({
        file,
        status: 'pending' as const
      }));
      
      setUploadQueue(prev => [...prev, ...newFiles]);
      
      // Reset the file input
      event.target.value = '';
    }
  };
  
  const handleDownload = async (documentId: number) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      let idToken: string | null = null;

      if (user) {
        idToken = await user.getIdToken();
      }

      if (!idToken) {
        throw new Error('Authentication failed: Please log in again');
      }

      const downloadUrl = ApiService.getDocumentDownloadUrl(documentId);
      const response = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${idToken}`, // Add the Firebase ID token
        },
      });
      if (response.status === 401) {
        throw new Error('Authentication failed: Please log in again');
      }
      if (!response.ok) {
        throw new Error(`Failed to download document: ${response.status} ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = documents.find(doc => doc.id === documentId)?.filename || `document-${documentId}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Download failed';
      setError(`Download failed: ${errorMessage}`);
      if (errorMessage.includes('Authentication failed')) {
        // Optionally, sign out user
        const auth = getAuth();
        auth.signOut();
        window.location.href = '/';
      }
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };
  
  // Navigate to previous page
  const goToPreviousPage = () => {
    if (pagination.currentPage > 0) {
      fetchDocuments(pagination.currentPage - 1);
    }
  };
  
  // Navigate to next page
  const goToNextPage = () => {
    const totalPages = Math.ceil(pagination.totalDocuments / pagination.limit);
    if (pagination.currentPage < totalPages - 1) {
      fetchDocuments(pagination.currentPage + 1);
    }
  };

  return (
    <div className="data-sources">
      <div className="data-sources-container">
        <div className="upload-section">
          <h2>Knowledge Base</h2>
          <p className="section-description">
            Upload text files to create a knowledge base for your chatbot.
            Currently only .txt files are supported.
          </p>
          
          <div className="upload-area">
            <label htmlFor="file-upload" className="upload-button">
              <FontAwesomeIcon icon={faUpload} /> Upload Text Files
            </label>
            <input 
              id="file-upload" 
              type="file" 
              accept=".txt" 
              multiple 
              onChange={handleFileUpload}
              style={{ display: 'none' }} 
            />
            <p className="upload-help">
              Maximum file size: 50MB per file <InfoTooltip text="Files will be processed and added to your chatbot's knowledge base" />
            </p>
          </div>
        </div>
        
        {/* Upload Queue Section */}
        {uploadQueue.length > 0 && (
          <div className="upload-queue">
            <h3>Upload Queue</h3>
            <ul className="queue-list">
              {uploadQueue.map((item, index) => (
                <li key={index} className={`queue-item status-${item.status}`}>
                  <div className="queue-item-main">
                    <span className="queue-item-icon">
                      {item.status === 'uploading' && <FontAwesomeIcon icon={faSpinner} spin />}
                      {item.status === 'success' && <FontAwesomeIcon icon={faCheck} />}
                      {item.status === 'error' && <FontAwesomeIcon icon={faTimes} />}
                      {item.status === 'pending' && <FontAwesomeIcon icon={faFile} />}
                    </span>
                    <span className="queue-item-name">{item.file.name}</span>
                    <span className="queue-item-size">{formatFileSize(item.file.size)}</span>
                  </div>
                  {item.message && <div className="queue-item-message">{item.message}</div>}
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Existing Documents Section */}
        <div className="documents-section">
          <h3>Your Documents</h3>
          
          {isLoading ? (
            <div className="loading-indicator">
              <FontAwesomeIcon icon={faSpinner} spin />
              <span>Loading documents...</span>
            </div>
          ) : error ? (
            <div className="error-message">
              <FontAwesomeIcon icon={faTimes} />
              <span>{error}</span>
            </div>
          ) : documents.length === 0 ? (
            <div className="no-documents">
              <p>You haven't uploaded any documents yet.</p>
            </div>
          ) : (
            <>
              <ul className="documents-list">
                {documents.map((doc) => (
                  <li key={doc.id} className="document-item">
                    <span className="document-icon">
                      <FontAwesomeIcon icon={faFile} />
                    </span>
                    <div className="document-details">
                      <span className="document-name">{doc.filename}</span>
                      <div className="document-meta">
                        <span className="document-size">{formatFileSize(doc.file_size)}</span>
                        <span className="document-date">Uploaded on {formatDate(doc.created_at)}</span>
                      </div>
                    </div>
                    <button 
                      className="download-button" 
                      onClick={() => handleDownload(doc.id)}
                      title="Download document"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                    </button>
                  </li>
                ))}
              </ul>
              
              {/* Pagination */}
              {pagination.totalDocuments > pagination.limit && (
                <div className="pagination-controls">
                  <button 
                    className="pagination-button" 
                    onClick={goToPreviousPage}
                    disabled={pagination.currentPage === 0}
                  >
                    <FontAwesomeIcon icon={faAngleLeft} /> Previous
                  </button>
                  
                  <span className="pagination-info">
                    Page {pagination.currentPage + 1} of {Math.ceil(pagination.totalDocuments / pagination.limit)}
                  </span>
                  
                  <button 
                    className="pagination-button" 
                    onClick={goToNextPage}
                    disabled={pagination.currentPage >= Math.ceil(pagination.totalDocuments / pagination.limit) - 1}
                  >
                    Next <FontAwesomeIcon icon={faAngleRight} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataSources;